import { useCallback } from "react";
import { useAuth } from "../../../context/AuthProvider";
import {
  createJourneyOrMilestone,
  isMilestoneResult,
  notifyJourneyChanged,
  patchJourney,
  type CreateJourneyBody,
  type CreateJourneyResult,
  type CreateMilestoneResult,
  type JourneyView
} from "../../../lib/journeyApi";
import { reclassifyEvent } from "../../../lib/eventsApi";
import type {
  RoleResolutionChoice,
  RoleResolutionContext
} from "../RoleResolutionSheet";

/**
 * `useRoleResolution` — the ONE shared confirm path (build-plan §2.2, Gap 4).
 *
 * Every `RoleResolutionSheet` resolution — from the add-event/apply funnel, the
 * I2 anon-reconcile banner, and the A5 promote/demote awareness actions — routes
 * its picked `choice` through `applyRoleResolution(choice, ctx)`. This is the
 * single source of truth for "what a pick does" so no surface reimplements the
 * create/reclassify/patch retry logic (the mapping table in §2.2).
 *
 * Each branch fires `notifyJourneyChanged()` on success so `useJourneys`
 * refetches and the caller (e.g. the reconcile banner) recomputes the next
 * pair (SF3: re-run the collision check until clean).
 *
 * Trust boundary (SF20): the server re-validates every resolution; this hook
 * only chooses the endpoint + body — it never assumes the pick is still valid.
 */

/** Extra data the caller threads alongside the sheet context so the hook can
 *  address the right journey/event. The sheet's `EventRef`s carry the Customer
 *  EVENT id; a `pair` between two EXISTING journeys (I2 reconcile) also needs the
 *  JOURNEY ids for chain/split window PATCHes and the earlier event for a nest. */
export type RoleResolutionExtras = {
  /** Map an event id → its active race journey (for I2/A5 where journeys exist). */
  journeyByEventId?: (eventId: string) => JourneyView | null;
  /** A create body to retry for the funnel's `nest_new` outcome (create-in-flight). */
  createRetryBody?: CreateJourneyBody;
  /** CSRF token + refresh — supplied by the caller's auth context when it differs. */
};

export type ApplyRoleResolution = (
  choice: RoleResolutionChoice,
  context: RoleResolutionContext,
  extras?: RoleResolutionExtras
) => Promise<void>;

export type UseRoleResolutionResult = {
  applyRoleResolution: ApplyRoleResolution;
  /** Thin reclassify wrapper (A4/A5): promote/demote a single event's role. */
  reclassify: (
    eventId: string,
    to: "goal" | "milestone",
    opts?: { intoJourneyId?: string }
  ) => Promise<{ intent: "goal" | "milestone" }>;
};

export function useRoleResolution(): UseRoleResolutionResult {
  const { state: authState, refreshCsrf } = useAuth();
  const csrfToken =
    authState.kind === "customer" || authState.kind === "anon" ? authState.csrfToken : null;

  const reclassify = useCallback<UseRoleResolutionResult["reclassify"]>(
    async (eventId, to, opts) => {
      if (!csrfToken) throw new Error("Couldn't change the race role — refresh and try again.");
      const res = await reclassifyEvent(eventId, to, csrfToken, refreshCsrf, opts);
      notifyJourneyChanged();
      return res;
    },
    [csrfToken, refreshCsrf]
  );

  const applyRoleResolution = useCallback<ApplyRoleResolution>(
    async (choice, context, extras) => {
      if (!csrfToken) throw new Error("Couldn't sort that out — refresh and try again.");

      switch (choice.kind) {
        // ── pair (add-event/apply collision, promote-collision, anon reconcile) ──
        case "pair": {
          if (context.kind !== "pair") throw new Error("role-resolution: kind mismatch");
          const [a, b] = context.events;
          const earlier = a.ymd <= b.ymd ? a : b;
          const later = a.ymd <= b.ymd ? b : a;

          switch (choice.outcome) {
            // nest_new: the NEW (earlier) event becomes a tune-up. When a create
            // is in flight (funnel) retry it with `resolveAsMilestone`; otherwise
            // (I2/A5, both events already exist) reclassify the earlier event.
            case "nest_new": {
              if (extras?.createRetryBody) {
                const res = await createJourneyOrMilestone(
                  { ...extras.createRetryBody, resolveAsMilestone: true },
                  csrfToken,
                  refreshCsrf
                );
                notifyJourneyChanged();
                void assertMilestone(res);
                return;
              }
              await reclassify(earlier.id, "milestone");
              return;
            }
            // nest_existing (demote): the EARLIER race becomes a tune-up inside the
            // later goal's build — its journey archives. reclassifyEvent runs the
            // same demote primitive S1's `overlapResolution:{mode:"demote"}` uses,
            // and works whether or not a create is in flight.
            case "nest_existing": {
              await reclassify(earlier.id, "milestone");
              return;
            }
            // chain: keep BOTH as goals. Only meaningful when a create is in
            // flight (the funnel) — the server resolves the geometry against the
            // existing journey. For two ALREADY-LIVE journeys (I2 reconcile) the
            // colliding-actives case only ever offers nest outcomes (the geometry
            // returns nest_* for overlapping same-direction actives), so chain
            // here is not reachable; guard it explicitly rather than silently no-op.
            case "chain": {
              if (!extras?.createRetryBody) {
                throw new Error(
                  "role-resolution: chain is only a create-time resolution (no create in flight)"
                );
              }
              const res = await createJourneyOrMilestone(
                { ...extras.createRetryBody, overlapResolution: { mode: "chain" } },
                csrfToken,
                refreshCsrf
              );
              notifyJourneyChanged();
              void assertMilestone(res);
              void earlier;
              void later;
              return;
            }
            default:
              return;
          }
        }

        // ── window_covers / window_orphans → a single journey PATCH (C3) ──
        case "window_covers": {
          if (context.kind !== "window_covers") throw new Error("role-resolution: kind mismatch");
          const { journeyId } = context;
          // clamp_start needs the clamped YMD, which the caller (C3) computes from
          // the covered event's date — the sheet only carries the eventId. C3 owns
          // that translation; here we forward the reclassify/pair paths.
          if (choice.action === "clamp_start") {
            throw new Error(
              "role-resolution: clamp_start needs the caller (C3) to supply the clamped start YMD"
            );
          }
          const patch =
            choice.action === "reclassify"
              ? { reclassify: [{ eventId: choice.eventId, to: "milestone" as const }] }
              : choice.action === "nest_existing"
                ? { pairResolution: [{ eventId: choice.eventId, mode: "demote" as const }] }
                : /* split */ { pairResolution: [{ eventId: choice.eventId, mode: "split" as const }] };
          await patchJourney(journeyId, patch, csrfToken, refreshCsrf);
          notifyJourneyChanged();
          return;
        }
        case "window_orphans": {
          if (context.kind !== "window_orphans") throw new Error("role-resolution: kind mismatch");
          const { journeyId } = context;
          if (choice.action === "keep_as_goal") {
            await patchJourney(
              journeyId,
              { reclassify: [{ eventId: choice.eventId, to: "goal" }] },
              csrfToken,
              refreshCsrf
            );
            notifyJourneyChanged();
            return;
          }
          // extend / remove are caller-owned (C3/C4): extend widens the window
          // (needs the widened YMD), remove deletes the event.
          throw new Error(
            `role-resolution: window_orphans '${choice.action}' is caller-owned (C3/C4)`
          );
        }

        // ── goal_removal → the caller's delete/archive path owns the disposition ──
        case "goal_removal": {
          throw new Error(
            "role-resolution: goal_removal disposition is applied by the delete/archive caller (C3/C4)"
          );
        }
        default:
          return;
      }
    },
    [csrfToken, refreshCsrf, reclassify]
  );

  return { applyRoleResolution, reclassify };
}

/** Discriminate the create union so a `nest_new` retry that resolved as a
 *  milestone doesn't get treated as a journey by callers reading the result. */
function assertMilestone(
  res: CreateJourneyResult | CreateMilestoneResult
): res is CreateMilestoneResult {
  return isMilestoneResult(res);
}
